/**
 * Formatery PL (PLAN-IMPLEMENTACJI §8.4 + formaty „maszynowe" ze spec 08):
 * kwoty PLN, daty, maskowanie NIP/PESEL/IBAN, pluralizacja polska.
 */

import { NBSP } from './nbsp'

/** Znak maskowania wrażliwych identyfikatorów (U+2022, wzór „521-••-••-321"). */
const BULLET = '•'

// ---------------------------------------------------------------------------
// Kwoty PLN
// ---------------------------------------------------------------------------

export type GroszeDisplay = 'auto' | 'always' | 'never'

export interface FormatPLNOptions {
  /**
   * Prezentacja końcówki groszowej:
   *  - 'auto' (domyślnie) — „,gg" tylko gdy grosze są niezerowe,
   *  - 'always' — zawsze „,gg",
   *  - 'never' — tylko pełne złote (zaokrąglenie połowy od zera: 50 gr → 1 zł).
   */
  grosze?: GroszeDisplay
}

const PLN_INT_FORMAT = new Intl.NumberFormat('pl-PL', {
  useGrouping: true,
  maximumFractionDigits: 0,
})

/**
 * Separator tysięcy z Intl bywa różny między wersjami ICU (U+0020 / U+2009 /
 * U+202F) — normalizujemy zawsze do twardej spacji U+00A0 (spec 08).
 */
function normalizeGroupSeparators(formatted: string): string {
  return formatted.replace(/[\u0020\u2009\u202F]/g, NBSP)
}

/**
 * Formatuje kwotę w groszach do postaci „1 284 600 zł" (spec 08: separator
 * tysięcy i odstęp przed „zł" to twarde spacje U+00A0, przecinek dziesiętny).
 *
 * @param grosze kwota w groszach; `number` musi być bezpieczną liczbą
 *   całkowitą — dla większych kwot podaj `bigint`.
 * @throws RangeError gdy `number` nie jest bezpieczną liczbą całkowitą.
 */
export function formatPLN(grosze: bigint | number, options: FormatPLNOptions = {}): string {
  if (typeof grosze === 'number' && !Number.isSafeInteger(grosze)) {
    throw new RangeError('formatPLN: grosze muszą być bezpieczną liczbą całkowitą (dla większych kwot użyj bigint)')
  }
  const mode = options.grosze ?? 'auto'
  const total = typeof grosze === 'bigint' ? grosze : BigInt(grosze)
  const negative = total < 0n
  const abs = negative ? -total : total

  let zl = abs / 100n
  const gr = Number(abs % 100n)

  let fraction = ''
  if (mode === 'always' || (mode === 'auto' && gr !== 0)) {
    fraction = ',' + String(gr).padStart(2, '0')
  } else if (mode === 'never') {
    // zaokrąglenie połowy od zera na wartości bezwzględnej, znak wraca niżej
    zl = (abs + 50n) / 100n
  }

  const sign = negative ? '-' : ''
  return `${sign}${normalizeGroupSeparators(PLN_INT_FORMAT.format(zl))}${fraction}${NBSP}zł`
}

// ---------------------------------------------------------------------------
// Liczby dziesiętne i godziny (fala 9, grupa 92)
// ---------------------------------------------------------------------------

export interface FormatDecimalOptions {
  /** Najmniej cyfr po przecinku (domyślnie 0 — „0,5", nie „0,50"). */
  minFractionDigits?: number
  /** Najwięcej cyfr po przecinku (domyślnie 2). */
  maxFractionDigits?: number
}

/**
 * Liczba dziesiętna w zapisie języka: „1 234,5" po polsku (przecinek, twarda
 * spacja tysięcy), „1,234.5" po angielsku. Wejście może być napisem — kolumny
 * `numeric` przychodzą z API jako „0.50", a ekran pokazywał je wprost.
 */
export function formatDecimal(
  value: number | string,
  lng: FormatLanguage = 'pl',
  options: FormatDecimalOptions = {},
): string {
  const number = typeof value === 'string' ? Number(value) : value
  if (!Number.isFinite(number)) {
    throw new RangeError(`formatDecimal: nieprawidłowa liczba „${String(value)}"`)
  }
  const formatted = new Intl.NumberFormat(lng === 'en' ? 'en-GB' : 'pl-PL', {
    useGrouping: true,
    minimumFractionDigits: options.minFractionDigits ?? 0,
    maximumFractionDigits: options.maxFractionDigits ?? 2,
  }).format(number)
  return lng === 'en' ? formatted : normalizeGroupSeparators(formatted)
}

/** Godziny: liczba i „h" przez twardą spację — „8 h", „29,5 h". */
export function formatHours(value: number | string, lng: FormatLanguage = 'pl'): string {
  return `${formatDecimal(value, lng)}${NBSP}h`
}

// ---------------------------------------------------------------------------
// Daty
// ---------------------------------------------------------------------------

export type DateVariant = 'dot' | 'long' | 'period' | 'iso'

/** Język formatera — ten sam zestaw co zasoby aplikacji. */
export type FormatLanguage = 'pl' | 'en'

/** Nazwy miesięcy w dopełniaczu — wariant 'long' po polsku: „5 czerwca 2026". */
const MONTHS_GENITIVE = [
  'stycznia',
  'lutego',
  'marca',
  'kwietnia',
  'maja',
  'czerwca',
  'lipca',
  'sierpnia',
  'września',
  'października',
  'listopada',
  'grudnia',
] as const

/** Wariant 'long' po angielsku w układzie en-GB: „5 June 2026" (fala 9, grupa 59). */
const MONTHS_EN = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const

interface DateParts {
  year: number
  month: number
  day: number
}

/** Data kalendarzowa — czytana dosłownie, bez stref. */
const DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})$/
/** Chwila ze strefą (`…T…Z` albo `…T…+02:00`) — dzień liczymy w Polsce. */
const ZONED_INSTANT_RE = /^\d{4}-\d{2}-\d{2}T.+(?:Z|[+-]\d{2}:?\d{2})$/
/** Data z godziną bez strefy — część datowa czytana dosłownie, jak dawniej. */
const LOCAL_DATETIME_RE = /^(\d{4})-(\d{2})-(\d{2})[T ].+$/

const WARSAW_PARTS = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Europe/Warsaw',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
})

/** Składowe chwili w strefie Europe/Warsaw (ADR-0055 §7). */
function warsawParts(instant: Date): DateParts & { hour: number; minute: number } {
  const parts = Object.fromEntries(
    WARSAW_PARTS.formatToParts(instant).map((part) => [part.type, part.value]),
  ) as Record<string, string>
  return {
    year: Number(parts.year),
    month: Number(parts.month),
    day: Number(parts.day),
    hour: Number(parts.hour) % 24,
    minute: Number(parts.minute),
  }
}

function checked(year: number, month: number, day: number, input: string): DateParts {
  if (month >= 1 && month <= 12 && day >= 1 && day <= 31) return { year, month, day }
  throw new RangeError(`formatDate: nieprawidłowa data „${input}" (oczekiwano ISO rrrr-mm-dd)`)
}

/**
 * Daty kadrowe to daty kalendarzowe: „rrrr-mm-dd" czytana jest dosłownie.
 * Chwila (znacznik czasu ze strefą albo obiekt Date) to zdarzenie — jego dzień
 * liczymy w strefie Europe/Warsaw, nie w UTC i nie w strefie przeglądarki:
 * `createdAt.slice(0, 10)` pokazywał dzień UTC, więc wpis z 00:30 w Polsce
 * trafiał na dzień wcześniej (fala 9, grupa 89; ADR-0055 §7).
 */
function toDateParts(input: Date | string): DateParts {
  if (typeof input === 'string') {
    const dateOnly = DATE_ONLY_RE.exec(input)
    if (dateOnly) return checked(Number(dateOnly[1]), Number(dateOnly[2]), Number(dateOnly[3]), input)
    if (ZONED_INSTANT_RE.test(input)) {
      const instant = new Date(input)
      if (!Number.isNaN(instant.getTime())) return warsawParts(instant)
    }
    const local = LOCAL_DATETIME_RE.exec(input)
    if (local) return checked(Number(local[1]), Number(local[2]), Number(local[3]), input)
    throw new RangeError(`formatDate: nieprawidłowa data „${input}" (oczekiwano ISO rrrr-mm-dd)`)
  }
  if (Number.isNaN(input.getTime())) {
    throw new RangeError('formatDate: nieprawidłowy obiekt Date')
  }
  return warsawParts(input)
}

function pad2(n: number): string {
  return String(n).padStart(2, '0')
}

/**
 * Formatuje datę w wariantach z makiet:
 *  - 'dot'    — „dd.mm.rrrr" (np. „05.06.2026"),
 *  - 'long'   — „5 czerwca 2026" po polsku albo „5 June 2026" po angielsku,
 *               spacje twarde; język jest WYMAGANY, bo dawniej EN dostawał
 *               polski dopełniacz (fala 9, grupa 59),
 *  - 'period' — „mm/rrrr" (okresy deklaracji, np. „06/2026"),
 *  - 'iso'    — „rrrr-mm-dd".
 */
export function formatDate(date: Date | string, variant: 'long', lng: FormatLanguage): string
export function formatDate(
  date: Date | string,
  variant: Exclude<DateVariant, 'long'>,
  lng?: FormatLanguage,
): string
export function formatDate(date: Date | string, variant: DateVariant, lng: FormatLanguage = 'pl'): string {
  const { year, month, day } = toDateParts(date)
  switch (variant) {
    case 'dot':
      return `${pad2(day)}.${pad2(month)}.${year}`
    case 'long':
      return lng === 'en'
        ? `${day}${NBSP}${MONTHS_EN[month - 1]}${NBSP}${year}`
        : `${day}${NBSP}${MONTHS_GENITIVE[month - 1]}${NBSP}${year}`
    case 'period':
      return `${pad2(month)}/${year}`
    case 'iso':
      return `${year}-${pad2(month)}-${pad2(day)}`
  }
}

/**
 * Data z godziną dla dziennika audytu — „05.06.2026, 14:32".
 *
 * Celowo bez czasu względnego („2 min temu") z makiety: dziennik ma być dowodem,
 * a dowód potrzebuje jednoznacznej chwili, nie zaokrąglenia [D30]. Od fali 9
 * godzina jest w strefie Europe/Warsaw, a nie przeglądarki — wpis z dziennika
 * czyta się tak samo z każdego miejsca (zmiana D30, ADR-0055 §7).
 */
export function formatDateTime(date: Date | string): string {
  const input = typeof date === 'string' ? new Date(date) : date
  if (Number.isNaN(input.getTime())) {
    throw new RangeError('formatDateTime: nieprawidłowa data')
  }
  const { year, month, day, hour, minute } = warsawParts(input)
  return `${pad2(day)}.${pad2(month)}.${year}, ${pad2(hour)}:${pad2(minute)}`
}

// ---------------------------------------------------------------------------
// Maskowanie wrażliwych identyfikatorów (spec 08: „środkowe grupy kropkami")
// ---------------------------------------------------------------------------

/**
 * Maskuje NIP do wzoru „521-••-••-321" — widoczne pierwsze 3 i ostatnie
 * 3 cyfry, środek maskowany w układzie 3-2-2-3. Separatory w wejściu są
 * ignorowane.
 *
 * @throws RangeError gdy wejście nie zawiera dokładnie 10 cyfr.
 */
export function maskNip(nip: string): string {
  const digits = nip.replace(/\D/g, '')
  if (digits.length !== 10) {
    throw new RangeError('maskNip: oczekiwano 10 cyfr NIP')
  }
  return `${digits.slice(0, 3)}-${BULLET}${BULLET}-${BULLET}${BULLET}-${digits.slice(-3)}`
}

/**
 * Maskuje PESEL — widoczne 2 pierwsze i 2 ostatnie cyfry, środek kropkami
 * (np. „02•••••••28").
 *
 * @throws RangeError gdy wejście nie zawiera dokładnie 11 cyfr.
 */
export function maskPesel(pesel: string): string {
  const digits = pesel.replace(/\D/g, '')
  if (digits.length !== 11) {
    throw new RangeError('maskPesel: oczekiwano 11 cyfr PESEL')
  }
  return `${digits.slice(0, 2)}${BULLET.repeat(7)}${digits.slice(-2)}`
}

/**
 * Maskuje polski IBAN — widoczne „PL", 2 pierwsze cyfry (kontrolne)
 * i 4 ostatnie; reszta kropkami. Wynik grupowany po 4 znaki twardymi
 * spacjami U+00A0. Prefiks „PL" w wejściu jest opcjonalny, odstępy ignorowane.
 *
 * @throws RangeError gdy wejście nie jest polskim IBAN-em (26 cyfr).
 */
export function maskIban(iban: string): string {
  const compact = iban.replace(/\s/g, '').toUpperCase()
  const match = /^(?:PL)?(\d{26})$/.exec(compact)
  const digits = match?.[1]
  if (!digits) {
    throw new RangeError('maskIban: oczekiwano polskiego IBAN (PL + 26 cyfr)')
  }
  const masked = `PL${digits.slice(0, 2)}${BULLET.repeat(20)}${digits.slice(-4)}`
  const groups = masked.match(/.{1,4}/g) ?? []
  return groups.join(NBSP)
}

// ---------------------------------------------------------------------------
// Pluralizacja polska
// ---------------------------------------------------------------------------

export interface PluralFormsPL {
  /** 1 (np. „dzień"). */
  one: string
  /** 2–4, 22–24, … poza 12–14 (np. „dni"). */
  few: string
  /** 0, 5–21, 25–31, … oraz ułamki (np. „dni" w dopełniaczu, „plików"). */
  many: string
}

/**
 * Wybiera polską formę liczby mnogiej (reguły CLDR dla pl):
 * 1 → one; końcówka 2–4 poza 12–14 → few; pozostałe (w tym 0, 12–14, 112)
 * → many. Wartości ujemne liczone z wartości bezwzględnej; niecałkowite
 * mapowane na many (spec przewiduje trzy formy).
 */
export function pluralPL(n: bigint | number, forms: PluralFormsPL): string {
  if (typeof n === 'number' && !Number.isInteger(n)) {
    return forms.many
  }
  const abs = typeof n === 'bigint' ? (n < 0n ? -n : n) : Math.abs(n)
  if (abs === 1 || abs === 1n) {
    return forms.one
  }
  const mod10 = typeof abs === 'bigint' ? Number(abs % 10n) : abs % 10
  const mod100 = typeof abs === 'bigint' ? Number(abs % 100n) : abs % 100
  if (mod10 >= 2 && mod10 <= 4 && !(mod100 >= 12 && mod100 <= 14)) {
    return forms.few
  }
  return forms.many
}
